import { useLayoutEffect } from 'react'
import useStore from '@src/store/common/global'

/**
 * 把 store.systemName / store.seo 同步到 document.title 与 <meta> 标签：
 *  - document.title                          ->  "{systemName} - {seo.title}"  （去重空白）
 *  - <meta name="description">               ->  seo.description
 *  - <meta name="keywords">                  ->  seo.keywords
 *  - #app-loading-system-name （初始加载层）  ->  systemName
 *
 * - 用 useLayoutEffect 在浏览器绘制前完成，避免「默认 title 闪一下再变成自定义」的抖动
 * - 在 Router 外侧挂载，确保整个生命周期生效
 * - meta 标签不存在时由代码创建（首次运行 / SSR hydration 也安全）
 * - 初始加载层文本在 React 还没挂载时由 index.html 内的脚本兜底读取 localStorage，
 *   此处负责「用户在主题设置里改名后」也立刻同步过去
 */
export const useDocumentMeta = () => {
	const systemName = useStore((s) => s.systemName)
	const seo = useStore((s) => s.seo)

	useLayoutEffect(() => {
		if (typeof document === 'undefined') return

		// 1. document.title
		const sysName = systemName.trim()
		const seoTitle = seo.title.trim()
		// 两个都有内容时拼成 "{sysName} - {seoTitle}"，避免出现 "Plus-One UI - " 这种空尾巴
		if (sysName && seoTitle) {
			document.title = `${sysName} - ${seoTitle}`
		} else {
			document.title = sysName || seoTitle || 'Plus-One UI'
		}

		// 2. <meta name="description">
		setMeta('description', seo.description.trim() || 'Plus-One UI')

		// 3. <meta name="keywords">
		setMeta('keywords', seo.keywords.trim() || 'Plus-One UI')

		// 4. 初始加载层的品牌名（如果还在 DOM 里；React 挂载后这一段早就淡出移除了）
		const loadingNameEl = document.getElementById('app-loading-system-name')
		if (loadingNameEl) {
			loadingNameEl.textContent = sysName || 'Plus One UI'
		}
	}, [systemName, seo])
}

/** 创建或更新一个 <meta name="..."> 标签的内容。 */
function setMeta(name: 'description' | 'keywords', content: string) {
	if (typeof document === 'undefined') return
	let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
	if (!el) {
		el = document.createElement('meta')
		el.name = name
		document.head.appendChild(el)
	}
	el.content = content
}
